import { useEffect, useRef, useState } from 'react'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'

/** How long filled-copy tooltip feedback stays before reverting. */
const kCopiedFeedbackMs = 1500

type UseMessageCopyArgs = {
  copy: () => Promise<void>
  analyticsEvent: AnalyticsEvent.CopyChatResponse | AnalyticsEvent.CopyChatUserMessage
  errorMessage: string
}

/**
 * Shared copy-to-clipboard UX for chat messages: analytics, loading,
 * temporary filled-copy tooltip feedback, and inline error text.
 */
export function useMessageCopy({ copy, analyticsEvent, errorMessage }: UseMessageCopyArgs) {
  const { trackEvent } = useAnalytics()
  const [isCopying, setIsCopying] = useState(false)
  const [isCopied, setIsCopied] = useState(false)
  const [copyError, setCopyError] = useState<string | null>(null)
  const copiedResetTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    return () => {
      if (copiedResetTimeoutRef.current) clearTimeout(copiedResetTimeoutRef.current)
    }
  }, [])

  function showCopiedFeedback() {
    setIsCopied(true)
    if (copiedResetTimeoutRef.current) clearTimeout(copiedResetTimeoutRef.current)
    copiedResetTimeoutRef.current = setTimeout(() => {
      setIsCopied(false)
      copiedResetTimeoutRef.current = null
    }, kCopiedFeedbackMs)
  }

  async function handleCopy() {
    if (isCopying) return

    trackEvent(analyticsEvent)
    setCopyError(null)
    setIsCopying(true)
    try {
      await copy()
      showCopiedFeedback()
    } catch {
      setCopyError(errorMessage)
    } finally {
      setIsCopying(false)
    }
  }

  return { isCopying, isCopied, copyError, handleCopy }
}
